import { Machine } from '../../gql/generated';
import { cn } from '../../utils/className';
import { formatMetricName, formatUnit } from '../../utils/formatMetric';

const layoutClasses = {
  // Each category reads like a row of the detail cards: its name as the label
  // and its metrics listed below it.
  list: {
    updated: 'pt-4',
    categories: 'divide-border-secondary flex flex-col divide-y',
    category: 'flex flex-col gap-1 py-4',
    heading: 'size-caption text-secondary font-regular',
    signals: 'flex flex-col gap-1',
    // The unit lines up on the right, so the units read as one column. That
    // position names it a unit, so it drops the brackets.
    signal: 'size-p2 text-primary flex items-baseline gap-1.5',
    name: 'min-w-0 flex-1 wrap-break-word',
    unit: 'text-secondary shrink-0 text-right',
  },
  grid: {
    updated: 'mb-4',
    categories: 'flex flex-col gap-6',
    category: '',
    heading: 'size-h4 text-primary mb-3 font-medium',
    signals: 'grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3',
    signal:
      'border-l-primary flex items-center gap-2 border-l-4 bg-gray-50 px-4 py-3',
    name: 'size-p1 text-primary font-medium',
    unit: 'size-p2 text-secondary',
  },
};

interface MachineSignalCategoriesProps {
  availableSignals: Machine['availableSignals'];
  /** `list` fits a narrow panel, `grid` the full-width desktop page. */
  layout: keyof typeof layoutClasses;
}

/** The signals a machine reports, grouped by category. */
export function MachineSignalCategories({
  availableSignals,
  layout,
}: MachineSignalCategoriesProps) {
  const categories = availableSignals?.signals;
  if (!categories || categories.length === 0) return null;

  const classes = layoutClasses[layout];
  // Each layout's heading sits one level below its section heading.
  const CategoryHeading = layout === 'grid' ? 'h3' : 'h4';

  return (
    <>
      {availableSignals.lastTimeUtc && (
        <p className={cn('size-p2 text-secondary', classes.updated)}>
          Last updated:{' '}
          {new Date(availableSignals.lastTimeUtc).toLocaleString()}
        </p>
      )}

      <div className={classes.categories}>
        {categories.map((category) => (
          <div key={category.category} className={classes.category}>
            <CategoryHeading className={classes.heading}>
              {category.category}
            </CategoryHeading>

            {category.signals && category.signals.length > 0 ? (
              <ul className={classes.signals}>
                {category.signals.map((signal) => (
                  <li key={signal.signal} className={classes.signal}>
                    <span className={classes.name}>
                      {formatMetricName(signal.signal)}
                    </span>
                    {signal.unit && (
                      <span className={classes.unit}>
                        {layout === 'list'
                          ? formatUnit(signal.unit)
                          : `(${formatUnit(signal.unit)})`}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className='size-p2 text-tertiary italic'>
                No metrics available in this category
              </p>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
